<script setup lang="ts">
import type { SubmitEventPromise } from 'vuetify';
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { reactive, ref, toRaw, watch } from 'vue';

import KNumberStepperField from '@/components/fields/KNumberStepperField.vue';
import KRadioField from '@/components/fields/KRadioField.vue';
import useForm from '@/composables/useForm';

export type LabelSize = openapi.PostReportReturnLabels_Body['size'];

export interface InitialData {
  size?: LabelSize;
  copies?: number;
}

export interface ValidData {
  size: LabelSize;
  copies: number;
}

export interface Props {
  loading?: boolean;
  initialData?: InitialData;
}
const props = withDefaults(defineProps<Props>(), {
  loading: false,
  initialData: () => ({}),
});
const emit = defineEmits<{
  submit: [event: SubmitEventPromise | null | undefined, formData: ValidData];
}>();

const smallSize: LabelSize = '4x4';
const largeSize: LabelSize = '4x6';
const defaultCopies = 1;
const maxCopies = 99;

const formData = reactive<ValidData>({
  size: smallSize,
  copies: defaultCopies,
});
const isValid = ref(true);

const { form, handleSubmit } = useForm(
  props,
  formData,
  function (event) {
    emit('submit', event, toRaw(formData));
  },
  { watcher: false },
);

watch(
  () => props.initialData,
  (newInitialData) => {
    formData.size = newInitialData.size ?? smallSize;
    formData.copies = newInitialData.copies ?? defaultCopies;
  },
  { immediate: true },
);
</script>

<template>
  <v-form
    ref="form"
    v-model="isValid"
    validate-on="lazy"
    :class="$style.returnLabelsPrintDialogForm"
    data-testid="returnLabelsPrintDialogForm"
    @submit.prevent="handleSubmit"
  >
    <v-radio-group
      v-model="formData.size"
      :disabled="props.loading"
      :class="$style.radioGroup"
      label="Label size"
      inline
      hide-details
    >
      <KRadioField :label="smallSize" :value="smallSize" />
      <KRadioField :label="largeSize" :value="largeSize" />
    </v-radio-group>
    <div :class="$style.copies">
      <span :class="$style.copiesLabel">Copies</span>
      <KNumberStepperField
        v-model="formData.copies"
        :min="1"
        :max="maxCopies"
        :disabled="props.loading"
        size="sm"
      />
    </div>
    <input type="submit" class="hidden" :disabled="props.loading" />
  </v-form>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.returnLabelsPrintDialogForm {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 2rem;
}
  .radioGroup {
    flex: none;
  }
  .copies {
    display: flex;
    align-items: center;
    gap: 0.8rem;
  }
    .copiesLabel {
      font-size: 14px;
      color: colors.$color-text-tertiary;
    }
</style>
